import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Crown, Check, Loader2, Shield, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// iter151 — Customer-facing plans now come from /api/membership-plans which is
// driven by the editable Admin → Settings → Memberships. The icon-by-key map
// stays here because icons aren't a meaningful "plan" attribute (no point
// storing them in settings) — they're just visual flair keyed by plan slug.
const PLAN_ICONS = {
  basic: Shield,
  protect: ShieldCheck,
  premier: Sparkles,
  silver: Shield,
  gold: ShieldCheck,
  platinum: Sparkles,
};

const Membership = () => {
  const [membership, setMembership] = useState(null);
  const [tiers, setTiers] = useState([]);  // iter151 — fetched from /api/membership-plans
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [loadingTier, setLoadingTier] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    fetchMembership();
    fetchPlans();
    checkPaymentStatus();
  }, []);

  const fetchPlans = async () => {
    try {
      const { data } = await axios.get(`${API}/membership-plans`);
      const plans = (data?.plans || []).map((p) => ({
        // Old field names some markup still references — keep both shapes.
        name: p.key,
        displayName: p.display_name,
        badge: p.badge || null,
        icon: PLAN_ICONS[p.key] || Crown,
        annualPrice: p.annual_price,
        monthlyPrice: p.monthly_price,
        annualOnly: !p.allow_monthly,
        subtitle: p.subtitle,
        description: p.description,
        features: p.perks || [],
      }));
      setTiers(plans);
    } catch (err) {
      console.error('Failed to load membership plans', err);
    }
  };

  const fetchMembership = async () => {
    try {
      const { data } = await axios.get(`${API}/membership`, { withCredentials: true });
      setMembership(data);
    } catch (error) {
      console.error('Error fetching membership:', error);
    }
  };

  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      setCheckingPayment(true);
      let attempts = 0;
      const maxAttempts = 5;

      const pollStatus = async () => {
        if (attempts >= maxAttempts) {
          toast.error('Payment verification timed out. Please refresh the page.');
          setCheckingPayment(false);
          return;
        }

        try {
          const { data } = await axios.get(`${API}/membership/checkout/status/${sessionId}`, { withCredentials: true });

          if (data.payment_status === 'paid') {
            toast.success('Membership activated successfully!');
            await fetchMembership();
            window.history.replaceState({}, document.title, window.location.pathname);
            setCheckingPayment(false);
            return;
          } else if (data.status === 'expired') {
            toast.error('Payment session expired. Please try again.');
            setCheckingPayment(false);
            return;
          }

          attempts++;
          setTimeout(pollStatus, 2000);
        } catch (error) {
          console.error('Error checking payment status:', error);
          toast.error('Error verifying payment. Please contact support.');
          setCheckingPayment(false);
        }
      };

      pollStatus();
    }
  };

  const handleCheckout = async (tier, period) => {
    setLoading(true);
    setLoadingTier(tier);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(
        `${API}/membership/checkout`,
        { tier, billing_period: period, origin_url: originUrl },
        { withCredentials: true }
      );

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout');
      setLoading(false);
      setLoadingTier(null);
    }
  };

  return (
    <div data-testid="membership-page" className="space-y-8">
      <div>
        <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-1 rounded-md text-xs font-bold tracking-[0.2em] mb-3">
          PREVENT+ MEMBERSHIP
        </div>
        <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-2">
          Prevent Problems. <span className="text-[#D4AF37]">Protect Your Home.</span>
        </h2>
        <p className="text-[#A3A3A3]">Annual plumbing maintenance. Priority service. Real savings.</p>
      </div>

      {checkingPayment && (
        <div data-testid="payment-processing-banner" className="bg-[#141414] border border-[#D4AF37] rounded-md p-6 flex items-center gap-4">
          <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
          <div>
            <p className="text-white font-medium">Processing your payment...</p>
            <p className="text-[#A3A3A3] text-sm">Please wait while we confirm your membership.</p>
          </div>
        </div>
      )}

      {membership && (
        <div data-testid="current-membership-card" className="bg-[#141414] border border-[#D4AF37] rounded-md p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-md flex items-center justify-center">
              <Crown className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-medium text-lg mb-1">Current Membership</h3>
              <p className="text-[#D4AF37] text-xl font-bold">
                {membership.tier?.toUpperCase()} - ${membership.monthly_fee?.toFixed(2)}
                {membership.billing_period === 'monthly' ? '/month' : '/year'}
              </p>
              <p className="text-[#A3A3A3] text-sm">Status: {membership.status}</p>
            </div>
          </div>
        </div>
      )}

      {/* Billing Period Toggle */}
      <div className="flex items-center justify-center">
        <div className="bg-[#141414] border border-[#262626] rounded-md p-1 inline-flex">
          <button
            data-testid="billing-annual-toggle"
            onClick={() => setBillingPeriod('annual')}
            className={`px-6 py-2 rounded-sm text-sm font-medium transition-all duration-200 ${
              billingPeriod === 'annual'
                ? 'bg-[#D4AF37] text-[#0A0A0A]'
                : 'text-[#A3A3A3] hover:text-white'
            }`}
          >
            Pay Annually
          </button>
          <button
            data-testid="billing-monthly-toggle"
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-2 rounded-sm text-sm font-medium transition-all duration-200 ${
              billingPeriod === 'monthly'
                ? 'bg-[#D4AF37] text-[#0A0A0A]'
                : 'text-[#A3A3A3] hover:text-white'
            }`}
          >
            Pay Monthly
          </button>
        </div>
      </div>

      {/* Benefits row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { title: 'Catch Small Issues', desc: 'Before they become expensive repairs' },
          { title: 'Priority Service', desc: 'Go to the front of the line' },
          { title: 'Member Discounts', desc: 'Save on repairs you may need' },
          { title: 'Peace of Mind', desc: 'Know your plumbing is in good hands' },
          { title: 'Built For Homeowners', desc: 'Plans designed to protect your home & budget' },
        ].map((b, i) => (
          <div key={i} className="bg-[#141414] border border-[#262626] rounded-md p-4">
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.15em] uppercase mb-2">{b.title}</p>
            <p className="text-[#A3A3A3] text-sm">{b.desc}</p>
          </div>
        ))}
      </div>

      {/* Tier Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
        {tiers.map((tier) => {
          const Icon = tier.icon;
          const isCurrentTier = membership?.tier === tier.name;
          const isAnnualOnly = tier.annualOnly;
          const effectivePeriod = isAnnualOnly ? 'annual' : billingPeriod;
          const displayPrice = effectivePeriod === 'annual' ? tier.annualPrice : tier.monthlyPrice;
          const periodLabel = effectivePeriod === 'annual' ? 'per year' : 'per month';
          const isLoading = loading && loadingTier === tier.name;

          return (
            <div
              key={tier.name}
              data-testid={`membership-tier-${tier.name}`}
              className={`bg-[#141414] rounded-md p-6 relative flex flex-col ${
                tier.badge === 'MOST POPULAR' ? 'border-2 border-[#D4AF37]' : 'border border-[#262626]'
              }`}
            >
              {tier.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className={`px-4 py-1 rounded-full text-xs font-bold tracking-wider ${
                    tier.badge === 'MOST POPULAR'
                      ? 'bg-[#D4AF37] text-[#0A0A0A]'
                      : 'bg-[#262626] text-[#D4AF37] border border-[#D4AF37]'
                  }`}>
                    {tier.badge}
                  </span>
                </div>
              )}

              <div className="text-center mb-4 pt-2">
                <div className="w-12 h-12 mx-auto mb-3 bg-[#D4AF37]/10 rounded-md flex items-center justify-center">
                  <Icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-['Outfit'] text-2xl font-bold text-white mb-1">{tier.displayName}</h3>
                <p className="text-[#A3A3A3] text-xs uppercase tracking-wider mb-3">{tier.subtitle}</p>
                <div className="text-[#D4AF37] text-4xl font-bold">${displayPrice}</div>
                <p className="text-[#A3A3A3] text-sm mt-1">{periodLabel}</p>
                {isAnnualOnly && (
                  <p className="text-[#A3A3A3] text-xs italic mt-1">Pay in full only</p>
                )}
              </div>

              <p className="text-[#A3A3A3] text-sm mb-4 text-center min-h-[40px]">{tier.description}</p>

              <ul className="space-y-2 mb-6 flex-1">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-white">
                    <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`select-${tier.name}-button`}
                onClick={() => handleCheckout(tier.name, effectivePeriod)}
                disabled={loading || isCurrentTier || checkingPayment}
                className={`w-full ${
                  tier.badge === 'MOST POPULAR'
                    ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]'
                    : 'bg-[#262626] text-white hover:bg-[#404040] border border-[#404040]'
                } ${isCurrentTier ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : isCurrentTier ? 'Current Plan' : `Get ${tier.displayName}`}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Value props */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-6 mt-8">
        <h3 className="font-['Outfit'] text-xl font-bold text-[#D4AF37] mb-4 text-center tracking-wider uppercase">The Value Speaks For Itself</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-[#D4AF37] text-2xl font-bold">$400 VALUE</p>
            <p className="text-white text-sm mt-1">Water Heater Flush</p>
          </div>
          <div className="text-center">
            <p className="text-[#D4AF37] text-2xl font-bold">$600 VALUE</p>
            <p className="text-white text-sm mt-1">Downspout Jetting</p>
          </div>
          <div className="text-center">
            <p className="text-[#D4AF37] text-2xl font-bold">Save $500-$2,600+</p>
            <p className="text-white text-sm mt-1">Most members save more than the cost of their plan each year</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Membership;
